import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ShiftAddress, ShiftNotes } from '@/components/shared/ShiftAddressNotes';
import { Clock, MapPin, PlayCircle, StopCircle, RefreshCw, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { clockInWindow, clockInClosedText } from '@/lib/clockInWindow';

// client#10: Progress Note and Incident Report items open their form for this shift (actionFor below)
const CLOCKOUT_CHECKLIST = [
  { key: 'progress_notes', label: 'Progress notes completed', action: 'progress_note' },
  { key: 'incident_reports', label: 'Incident and behaviour reports completed (if applicable)', action: 'incident' },
  { key: 'medication_charts', label: 'Medication and health charts updated (if applicable)' },
  { key: 'handover', label: 'Handover completed and outstanding actions communicated' },
  { key: 'followup', label: 'Follow-up actions, appointments, and mileage/trip log recorded (if applicable)' },
  { key: 'confirm_all', label: 'I confirm all participant care tasks have been completed or appropriately handed over.' },
];

/** The form a checklist item asks about, for this shift: Progress Notes on the shift page, or a pre-filled incident report. */
export function actionFor(item, shift) {
  if (!shift) return null;
  if (item.action === 'progress_note') {
    return { label: 'Write progress note', icon: FileText, to: `/staff/shift/${shift.id}#progress-notes` };
  }
  if (item.action === 'incident') {
    const q = new URLSearchParams({ shift_id: shift.id, return_to: '/my/clock' });
    if (shift.participant_id) q.set('participant_id', shift.participant_id);
    if (shift.participant_name) q.set('participant_name', shift.participant_name);
    if (shift.address || shift.location) q.set('location', shift.address || shift.location);
    return { label: 'Report incident', icon: AlertTriangle, to: `/compliance/incidents/new?${q.toString()}` };
  }
  return null;
}

/**
 * Minutes early/late vs the scheduled start (null inside the 15-min grace window).
 * Positive = late. Overnight shifts roll the end time to the next day.
 */
const VARIANCE_GRACE_MIN = 15;

const startMs = (s) => {
  const ms = new Date(`${s.date}T${(s.start_time || '00:00')}`).getTime();
  return Number.isFinite(ms) ? ms : null;
};
const endMs = (s) => {
  if (!s.end_time) return null;
  const ms = new Date(`${s.date}T${s.end_time}`).getTime();
  if (!Number.isFinite(ms)) return null;
  const st = startMs(s);
  return st !== null && ms <= st ? ms + 86400000 : ms;
};

function clockInVariance(s) {
  const st = s ? startMs(s) : null;
  if (st === null) return null;
  const delta = Math.round((Date.now() - st) / 60000);
  if (delta > VARIANCE_GRACE_MIN) return { kind: 'late', minutes: delta };
  if (delta < -VARIANCE_GRACE_MIN) return { kind: 'early', minutes: -delta };
  return null;
}

function clockOutVariance(s) {
  const en = s ? endMs(s) : null;
  if (en === null) return null;
  const delta = Math.round((Date.now() - en) / 60000);
  return delta > VARIANCE_GRACE_MIN ? { kind: 'late', minutes: delta } : null;
}

/**
 * Dedicated web Clock In/Out page.
 * Shows the SW's current (or next upcoming) shift, with prominent
 * Clock In / Clock Out actions. Uses /api/roster/shifts/{id}/check-in and
 * /check-out (same endpoints used by the shift detail page + mobile).
 */
export default function MyClockPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(null); // shift id currently being clocked
  const [checks, setChecks] = useState({});
  const [varianceReason, setVarianceReason] = useState('');
  const [needManual, setNeedManual] = useState(false); // device gave no location -> MANUAL exception with a reason
  const [manualReason, setManualReason] = useState('');
  const [, setTick] = useState(0); // 30s tick re-evaluates the variance window
  const allChecked = CLOCKOUT_CHECKLIST.every((c) => checks[c.key]);
  const toggleCheck = (key) => setChecks((prev) => ({ ...prev, [key]: !prev[key] }));

  const load = useCallback(async () => {
    try {
      const res = await api.get('/roster/my-shifts', { params: { page: 1, page_size: 20 } });
      setShifts(res.data.items || res.data.data || []);
    } catch (e) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onVisible = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', load);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const attendance = (s) => (s.staff_attendance || {})[user?.id] || {};
  const isCheckedIn = (s) => !!(attendance(s).check_in_time || s.check_in_time);
  const isCheckedOut = (s) => !!(attendance(s).check_out_time || s.check_out_time);

  // "Current" = in-progress or today's next; else first upcoming
  const today = new Date().toISOString().slice(0, 10);
  const active = shifts.find((s) => s.status === 'IN_PROGRESS' || (isCheckedIn(s) && !isCheckedOut(s)))
    || shifts.find((s) => s.date === today && !isCheckedOut(s))
    || shifts.find((s) => s.date >= today && !isCheckedOut(s));

  // Clock-in window: 2 h before start until the shift ends (the server enforces it). Outside it Clock In is disabled and
  // no early/late reason is asked for — there is nothing to clock in to yet (or any more).
  const nowMs = Date.now();   // the page's 30 s tick re-renders, so the window opens on its own
  const inWindow = active && !isCheckedIn(active) ? clockInWindow(active, nowMs) : null;
  const clockInClosed = !!inWindow && ['before', 'ended'].includes(inWindow.state);
  const inVar = active && !isCheckedIn(active) && !clockInClosed ? clockInVariance(active) : null;
  const outVar = active && isCheckedIn(active) && !isCheckedOut(active) ? clockOutVariance(active) : null;
  const varianceBlocked = (!!inVar || !!outVar) && !varianceReason.trim();

  const askGeo = () => new Promise((resolve) => {
    if (!('geolocation' in navigator)) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve({}),
      { enableHighAccuracy: true, timeout: 6000 },
    );
  });

  const manualBlocked = needManual && !manualReason.trim();
  // API errors arrive as {error: {message}} (or the older {detail}); never show a bare "failed".
  const errMsg = (e, fallback) => {
    const d = e.response?.data;
    return d?.error?.message || d?.detail?.message || (typeof d?.detail === 'string' ? d.detail : '') || fallback;
  };

  // Location body: real GPS when the device gives it; otherwise the MANUAL exception
  // with the worker's reason (the backend refuses missing coordinates and never fakes them).
  const locationBody = async () => {
    if (needManual) return { method: 'MANUAL', manualReason: manualReason.trim() };
    const geo = await askGeo();
    if (geo.lat == null || geo.lng == null) {
      setNeedManual(true);
      toast.error('Your location could not be obtained. Enter a reason below to clock in/out as a manual exception.');
      return null;
    }
    return { gpsLocation: geo };
  };

  const clockIn = async (s) => {
    if (varianceBlocked || manualBlocked) return;
    setBusy(s.id);
    try {
      const loc = await locationBody();
      if (!loc) return;
      const clientActionId = `web-${s.id}-${Date.now()}`;
      await api.post(`/roster/shifts/${s.id}/check-in`, {
        clientActionId,
        deviceTimestamp: new Date().toISOString(),
        ...loc,
        varianceReason: inVar ? varianceReason.trim() : undefined,
      });
      toast.success('Checked in');
      setVarianceReason('');
      setNeedManual(false); setManualReason('');
      await load();
    } catch (e) {
      toast.error(errMsg(e, 'Check-in failed'));
    } finally { setBusy(null); }
  };

  const clockOut = async (s) => {
    if (!allChecked || varianceBlocked || manualBlocked) return;
    setBusy(s.id);
    try {
      const loc = await locationBody();
      if (!loc) return;
      const clientActionId = `web-${s.id}-${Date.now()}`;
      await api.post(`/roster/shifts/${s.id}/check-out`, {
        clientActionId,
        deviceTimestamp: new Date().toISOString(),
        ...loc,
        varianceReason: outVar ? varianceReason.trim() : undefined,
      });
      toast.success('Checked out — timesheet submitted');
      setVarianceReason('');
      setNeedManual(false); setManualReason('');
      await load();
    } catch (e) {
      toast.error(errMsg(e, 'Check-out failed'));
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-clock-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Clock In / Out</h1>
          <p className="text-muted-foreground mt-1">Start or end your current shift</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { setRefreshing(true); load(); }} disabled={refreshing} data-testid="my-clock-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      {loading ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">Loading…</CardContent></Card>
      ) : !active ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Clock className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No current or upcoming shift to clock into.</p>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="my-clock-active-shift">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-lg">{active.participant_name || 'Shift'}</CardTitle>
                {/* client#6: no Service / Status for staff */}
                <div className="mt-1"><ShiftAddress shift={active} compact /></div>
                {/* client#21: read the shift notes before clocking in */}
                <div className="mt-1" data-testid="my-clock-notes"><ShiftNotes notes={active.notes} compact /></div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 text-sm mb-4">
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" /><span className="font-mono tabular-nums">{active.date} · {active.start_time}-{active.end_time}</span></div>
              {(active.state_id || active.branch_id) && (
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /><span>{[active.state_id, active.branch_id].filter(Boolean).join(' · ')}</span></div>
              )}
            </div>
            {isCheckedIn(active) && !isCheckedOut(active) && (
              <div className="mb-4 rounded-lg border p-3 space-y-1" data-testid="clockout-checklist">
                <p className="text-sm font-semibold mb-2">Before you clock out</p>
                {CLOCKOUT_CHECKLIST.map((item) => {
                  const act = actionFor(item, active);
                  return (
                    <div key={item.key} className="flex items-start justify-between gap-2">
                      <label className="flex items-start gap-3 py-1.5 cursor-pointer flex-1" data-testid={`clockout-check-${item.key}`}>
                        <Checkbox checked={!!checks[item.key]} onCheckedChange={() => toggleCheck(item.key)} className="mt-0.5" />
                        <span className="text-sm leading-snug">{item.label}</span>
                      </label>
                      {act && (
                        <Button type="button" size="sm" variant="outline" className="h-7 shrink-0 mt-1" onClick={() => navigate(act.to)} data-testid={`clockout-action-${item.key}`}>
                          <act.icon className="h-3.5 w-3.5 mr-1" />{act.label}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {clockInClosed && (
              <p className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm flex items-center gap-2" data-testid="my-clock-in-closed">
                <Clock className="h-4 w-4 shrink-0" />{clockInClosedText(inWindow)}
              </p>
            )}
            {(inVar || outVar) && (
              <div className="mb-4 space-y-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="clock-variance-block">
                <Label htmlFor="clock-variance-reason" className="text-sm font-semibold">
                  {inVar
                    ? (inVar.kind === 'early'
                      ? `Early clock-in reason — ${inVar.minutes} min before start (required)`
                      : `Late clock-in reason — ${inVar.minutes} min after start (required)`)
                    : `Late clock-out reason — ${outVar.minutes} min after end (required)`}
                </Label>
                <Textarea
                  id="clock-variance-reason"
                  rows={3}
                  value={varianceReason}
                  onChange={(e) => setVarianceReason(e.target.value)}
                  placeholder={inVar
                    ? (inVar.kind === 'early' ? 'Why are you starting early?' : 'Why are you starting late?')
                    : 'Why are you finishing later than the scheduled end?'}
                  data-testid="clock-variance-reason-input"
                />
                <p className="text-[11px] text-muted-foreground">This is recorded as an attendance exception for your coordinator to review.</p>
              </div>
            )}
            {needManual && !isCheckedOut(active) && (
              <div className="mb-4 space-y-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="clock-manual-block">
                <Label htmlFor="clock-manual-reason" className="text-sm font-semibold">Location unavailable — reason (required)</Label>
                <Textarea
                  id="clock-manual-reason"
                  rows={2}
                  value={manualReason}
                  onChange={(e) => setManualReason(e.target.value)}
                  placeholder="e.g. GPS signal unavailable inside the building"
                  data-testid="clock-manual-reason-input"
                />
                <p className="text-[11px] text-muted-foreground">Your device didn't share a location, so this is recorded as a manual exception for your coordinator to review.</p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              {!isCheckedIn(active) ? (
                <Button className="w-full" onClick={() => clockIn(active)} disabled={busy === active.id || varianceBlocked || manualBlocked || clockInClosed} data-testid="my-clock-in-btn">
                  <PlayCircle className="h-4 w-4 mr-2" />{busy === active.id ? 'Clocking in…' : 'Clock In'}
                </Button>
              ) : !isCheckedOut(active) ? (
                <Button className="w-full" variant="destructive" onClick={() => clockOut(active)} disabled={busy === active.id || !allChecked || varianceBlocked || manualBlocked} data-testid="my-clock-out-btn">
                  <StopCircle className="h-4 w-4 mr-2" />{busy === active.id ? 'Clocking out…' : 'Confirm & Clock Out'}
                </Button>
              ) : (
                <Button className="w-full" variant="outline" disabled data-testid="my-clock-done">Shift completed</Button>
              )}
              <Button variant="outline" onClick={() => navigate(`/staff/shift/${active.id}`)} data-testid="my-clock-open-detail">
                View shift details
              </Button>
            </div>
            {isCheckedIn(active) && !isCheckedOut(active) && !allChecked && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="clockout-gate-hint">Complete all items to confirm clock-out.</p>
            )}
            {(varianceBlocked || manualBlocked) && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="clock-variance-gate-hint">Enter a reason to continue.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
